/**
 * schemaToExample — walk a JSON Schema (as produced by zod-to-json-schema)
 * and synthesize a plausible example payload. Used by the Trigger modal's
 * JSON input to pre-fill the editor so users see *what shape* the
 * endpoint expects rather than a blank "{}".
 *
 * Preference order for each field:
 *   1. schema.default          ← author already declared the canonical example
 *   2. schema.examples[0]      ← Zod's .describe() can hang examples here
 *   3. schema.const            ← single literal
 *   4. type-based stub         ← '' / 0 / false / [] / {}
 *
 * Required-only mode (`mode: 'required'`) emits only required fields, so
 * the example is a minimal payload the user can submit as-is. Default
 * (`mode: 'full'`) emits every declared field, so users see the full
 * surface and can delete what they don't need.
 *
 * Refuses to recurse infinitely on $ref-cycles; bails out at depth 4.
 */

const MAX_DEPTH = 4;

// Fields that are AGENT-LEVEL config — chosen once at deploy (the Agent & model
// editor), NOT per-run input — so the trigger/schedule seed never pre-fills them
// (they'd just duplicate a setting the user already made). `model` is the LLM
// model override (defaults to "auto" = use the agent's deploy-time model).
const AGENT_LEVEL_FIELDS = new Set(['model']);

// ── Field-class conventions ──────────────────────────────────────────────────
// Everything is keyed off the field's `.describe(...)` text (lower-cased +
// trimmed, matched as a START-OF-STRING prefix) so this stays GENERIC — no
// hardcoded field names, any template opts in just by prefixing its
// description. Three classes:
//
//   HIDDEN   — never shown in the cloud manual-trigger modal at all:
//     - "self-host only"     → threaded in only by the self-hosted runner.
//     - "comment_reply only" → set only by the webhook on the in-thread reply.
//   ADVANCED — valid cloud input, but collapsed behind an "Advanced" expander
//              (hidden by default; power-users opt in):
//     - "advanced:" / "advanced —" / "advanced -" / "advanced "
//   NORMAL   — everything else; shown inline.
//
// A field's class is decided by isTriggerHidden() > isAdvancedField() > normal.
// Required fields are NEVER treated as advanced/hidden by class alone — the
// caller (JsonInput) guards required fields back in regardless of class, so a
// REQUIRED field that an author mistakenly prefixed "Advanced:" still renders.
const TRIGGER_HIDDEN_PREFIXES = ['self-host only', 'comment_reply only'];
// "advanced" as a leading token: "advanced:" / "advanced —" / "advanced -" /
// "advanced " (a word boundary), but NOT a word that merely STARTS with
// "advanced" like "advancedmetering" — require a separator after the token.
const ADVANCED_PREFIX_RE = /^advanced(?=[\s:—-]|$)/;

/**
 * isTriggerHidden — true when a property's description marks it as not
 * user-fillable in the cloud manual-trigger modal (see TRIGGER_HIDDEN_PREFIXES).
 *
 * Robust: only a string description that STARTS WITH one of the exact phrases
 * (case-insensitive) is hidden. A field without a description is never hidden,
 * so required fields that happen to lack a description stay visible.
 *
 * @param {object} propSchema  a single property's JSON Schema node
 * @returns {boolean}
 */
export function isTriggerHidden(propSchema) {
  const desc = propSchema?.description;
  if (typeof desc !== 'string') return false;
  const normalized = desc.trim().toLowerCase();
  return TRIGGER_HIDDEN_PREFIXES.some((p) => normalized.startsWith(p));
}

/**
 * isAdvancedField — true when a property's description marks it ADVANCED
 * (collapsed by default behind an expander). Mutually distinct from hidden:
 * a hidden field is never advanced (hidden wins).
 *
 * @param {object} propSchema  a single property's JSON Schema node
 * @returns {boolean}
 */
export function isAdvancedField(propSchema) {
  const desc = propSchema?.description;
  if (typeof desc !== 'string') return false;
  if (isTriggerHidden(propSchema)) return false;
  return ADVANCED_PREFIX_RE.test(desc.trim().toLowerCase());
}

/**
 * fieldVisibility — single source of truth for a property's display class.
 * @returns {'hidden'|'advanced'|'normal'}
 */
export function fieldVisibility(propSchema) {
  if (isTriggerHidden(propSchema)) return 'hidden';
  if (isAdvancedField(propSchema)) return 'advanced';
  return 'normal';
}

// ── Discriminated-union (multi-scenario) detection ───────────────────────────
// A zod `z.discriminatedUnion(tag, [...])` serializes (Zod v4 `z.toJSONSchema`)
// to a top-level `oneOf` of object branches; a plain `z.union([...])` of
// literal-tagged objects serializes to `anyOf`. In BOTH, each branch is
// `{ type:'object', properties }` and shares ONE property that is a single
// literal (`const`, or a 1-element `enum`) — that's the discriminator.
//
// We only treat a schema as a "tab schema" when EVERY branch is such an object
// AND they share a common discriminator property whose value is a distinct
// literal per branch. This is a STRICT test: a flat object schema (no
// oneOf/anyOf at top level) — i.e. every current template, incl. the flat
// `trigger: z.enum([...])` sentry-triage one — returns null and renders
// exactly as today. Only a real discriminated union opts into tabs.

const PREFERRED_DISCRIMINATORS = ['trigger', 'scenario', 'mode', 'action', 'type', 'kind'];

/** Literal value of a property iff it's a single const / 1-enum string. */
function singleLiteral(propSchema) {
  if (!propSchema || typeof propSchema !== 'object') return undefined;
  if (propSchema.const !== undefined) return propSchema.const;
  if (Array.isArray(propSchema.enum) && propSchema.enum.length === 1) return propSchema.enum[0];
  return undefined;
}

/**
 * detectUnion — returns null for a flat schema, or, for a discriminated-union
 * schema, a descriptor for rendering one tab per variant:
 *   {
 *     discriminator: 'trigger',
 *     variants: [
 *       { value: 'triage', label: 'Triage', schema: <flat object schema for this branch> },
 *       { value: 'fix',    label: 'Fix',    schema: <flat object schema for this branch> },
 *     ],
 *   }
 *
 * Each variant `schema` is the branch object schema with the discriminator
 * still present (as a const) — so the seeded example pins trigger:'<value>'
 * and the rest of the branch's fields render like any flat schema.
 *
 * @param {object|null} schema  JSON Schema (zod-to-json-schema output)
 * @returns {null | { discriminator: string, variants: Array<{value:any,label:string,schema:object}> }}
 */
export function detectUnion(schema) {
  if (!schema || typeof schema !== 'object') return null;
  const branches = schema.oneOf || schema.anyOf;
  if (!Array.isArray(branches) || branches.length < 2) return null;

  // Every branch must be an object schema with properties.
  if (!branches.every((b) => b && b.type === 'object' && b.properties && typeof b.properties === 'object')) {
    return null;
  }

  // Find a property name that is a SINGLE LITERAL in every branch, with a
  // distinct value per branch. Prefer the conventional discriminator names.
  const commonKeys = branches.reduce(
    (acc, b) => acc.filter((k) => Object.prototype.hasOwnProperty.call(b.properties, k)),
    Object.keys(branches[0].properties),
  );
  const candidates = [
    ...PREFERRED_DISCRIMINATORS.filter((k) => commonKeys.includes(k)),
    ...commonKeys.filter((k) => !PREFERRED_DISCRIMINATORS.includes(k)),
  ];

  for (const key of candidates) {
    const literals = branches.map((b) => singleLiteral(b.properties[key]));
    if (literals.some((v) => v === undefined)) continue;
    if (new Set(literals.map((v) => String(v))).size !== branches.length) continue;
    // Good discriminator.
    return {
      discriminator: key,
      variants: branches.map((b, i) => ({
        value: literals[i],
        label: humanizeLabel(literals[i]),
        schema: b,
      })),
    };
  }
  return null;
}

/** "fix" → "Fix", "comment_reply" → "Comment Reply". */
function humanizeLabel(value) {
  const s = String(value ?? '').trim();
  if (!s) return 'Variant';
  return s
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Field whose presence makes it the PRIMARY input — rendered/seeded FIRST so
// it reads as the main thing to fill. Generic: any object property literally
// named `instruction` is hoisted to the front of the generated example.
const PRIMARY_FIELD = 'instruction';

/**
 * orderKeys — return object keys with the primary field (`instruction`) first,
 * the rest in their original (schema-declared) order. Non-destructive: only
 * reorders if the primary field is present.
 */
function orderKeys(keys) {
  if (!keys.includes(PRIMARY_FIELD)) return keys;
  return [PRIMARY_FIELD, ...keys.filter((k) => k !== PRIMARY_FIELD)];
}

function exampleFor(schema, depth = 0, mode = 'full', hideInternal = false, hideAdvanced = false) {
  if (!schema || typeof schema !== 'object' || depth > MAX_DEPTH) return null;

  // Authored hints win first.
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) return schema.examples[0];
  if (schema.const !== undefined) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  const t = schema.type;

  if (t === 'string') {
    if (schema.format === 'uri' || schema.format === 'url') return 'https://example.com';
    if (schema.format === 'email') return 'user@example.com';
    if (schema.format === 'date-time') return new Date().toISOString();
    return '';
  }
  if (t === 'integer') return 0;
  if (t === 'number') return 0;
  if (t === 'boolean') return false;

  if (t === 'array') {
    // Emit one example element so the shape is visible, not just `[]`.
    if (schema.items) {
      const item = exampleFor(schema.items, depth + 1, mode, hideInternal, hideAdvanced);
      return item === null ? [] : [item];
    }
    return [];
  }

  if (t === 'object' && schema.properties) {
    const out = {};
    const required = new Set(schema.required || []);
    // instruction-first ordering: the primary field leads the generated object.
    for (const k of orderKeys(Object.keys(schema.properties))) {
      const v = schema.properties[k];
      const isRequired = required.has(k) && v.default === undefined;
      // A field with a `default` is satisfiable WITHOUT user input (the default
      // applies), so for the trigger payload it's effectively OPTIONAL even when
      // the JSON-schema `required` array lists it (Zod `.default()` fields land
      // in `required` but shouldn't need to be filled). Required-only mode emits
      // only fields that are required AND have no default.
      if (mode === 'required' && (!required.has(k) || v.default !== undefined)) continue;
      // 'seed' mode (the trigger/schedule editor default): show the agent's real
      // INPUT so it's discoverable + editable — required fields PLUS any field
      // that has a default (its configured value is shown, e.g. since:'weekly',
      // maxCandidates:30, the rubric) — but DROP two kinds of noise:
      //   • AGENT-LEVEL config that's chosen at deploy, not per run (model);
      //   • pure-optional fields with no default (they'd seed as empty stubs).
      // This keeps a config-only agent (github-ai-scout: all defaults) from
      // seeding an empty {}, while a fix-style agent still seeds just its
      // required input (sentry fix → { instruction, trigger }).
      if (mode === 'seed') {
        if (AGENT_LEVEL_FIELDS.has(k)) continue;
        if (!required.has(k) && v.default === undefined) continue;
      }
      // Opt-in: omit trigger-hidden props (self-host / comment_reply) from the
      // generated payload so a manual trigger only shows fillable fields.
      if (hideInternal && isTriggerHidden(v)) continue;
      // Opt-in: collapse ADVANCED props out of the default seed — but NEVER drop
      // a required field (it must stay fillable).
      if (hideAdvanced && !isRequired && isAdvancedField(v)) continue;
      const val = exampleFor(v, depth + 1, mode, hideInternal, hideAdvanced);
      if (val !== null) out[k] = val;
    }
    return out;
  }

  // anyOf / oneOf — pick the first branch. (A top-level discriminated union is
  // resolved upstream by detectUnion(); this fallback covers nested unions.)
  for (const key of ['anyOf', 'oneOf', 'allOf']) {
    if (Array.isArray(schema[key]) && schema[key].length > 0) {
      return exampleFor(schema[key][0], depth + 1, mode, hideInternal, hideAdvanced);
    }
  }

  return null;
}

/**
 * Public entrypoint. Returns a JS object (or `{}` for null/empty schemas).
 *
 * @param {object|null} schema  JSON Schema (zod-to-json-schema output)
 * @param {object}      opts
 * @param {string}      [opts.mode='full']        'full' | 'required'
 * @param {boolean}     [opts.hideInternal=false] when true, omit trigger-hidden
 *        props (self-host-only / comment_reply-only) recursively. Off by
 *        default so existing callers are unaffected.
 * @param {boolean}     [opts.hideAdvanced=false] when true, omit ADVANCED-class
 *        props (collapsed behind the modal's "Advanced" expander) from the
 *        generated payload — except required fields, which always stay. Off by
 *        default so existing callers are unaffected.
 */
export function schemaToExample(schema, { mode = 'full', hideInternal = false, hideAdvanced = false } = {}) {
  if (!schema) return {};
  const result = exampleFor(schema, 0, mode, hideInternal, hideAdvanced);
  return result && typeof result === 'object' && !Array.isArray(result) ? result : {};
}

/**
 * Pretty-print a value as 2-space-indented JSON, never throwing on
 * circular refs (uses a safe replacer).
 */
export function prettyJson(value) {
  const seen = new WeakSet();
  return JSON.stringify(value, (_k, v) => {
    if (typeof v === 'object' && v !== null) {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  }, 2);
}
